<template>
  <div class="reporting-config-main">
    <v-layout row wrap mx-0>
      <v-flex xs12 md6 class="pr-md-4">
        <h2 class="mb-2">{{ $t('reporting.config.goLiveWorkpackages') }}</h2>
        <workpackage-selector
          v-model="selectedWorkpackages"
          :max-selections="maxWorkpackageSelections"
          @menu-closed="onWpMenuClosed"
        />
        <v-alert
          v-if="hasOverlappingPricingGroups"
          :value="true"
          color="error"
          icon="warning"
          outlined
          class="mt-2"
        >
          <div v-for="(pg, index) in overlappingPricingGroups" :key="index" class="overlap-error">
            "{{ pg.pricingGroupName }}"
            {{ $t('reporting.config.pricingGroupOverlapMessage') }}
            (
            <template v-for="(wpName, wpIndex) in pg.workpackageNames">
              <a
                :key="pg.workpackageIds[wpIndex]"
                :href="getWorkpackageUrl(pg.workpackageIds[wpIndex])"
                target="_blank"
                class="overlap-wp-link"
              >
                <strong>{{ wpName }}</strong>
              </a>
              <span v-if="wpIndex < pg.workpackageNames.length - 1" :key="'sep-' + wpIndex">
                ,
              </span>
            </template>
            )
          </div>
        </v-alert>
        <v-alert
          v-if="!hasMatchingPricingGroups && approvalStatus && selectedWorkpackageIds.length > 0"
          :value="true"
          color="warning"
          icon="info"
          outlined
          class="mt-2"
        >
          {{
            approvalStatus === 'approved'
              ? $t('reporting.config.noApprovedPricingGroups')
              : $t('reporting.config.noNotApprovedPricingGroups')
          }}
        </v-alert>
      </v-flex>

      <v-flex xs12 md6 class="pl-md-4">
        <h2 class="mb-2">{{ $t('reporting.config.keyAttributes') }}</h2>
        <attribute-selector
          ref="attributeSelector"
          v-model="selectedAttributes"
          :available-attributes="hasWorkpackagesSelected ? availableAttributes : []"
          :max-selections="maxAttributeSelections"
          :max-values="maxAttributeValueLimit"
          :loading="loading"
        />

        <div class="mt-6">
          <h2 class="mb-2">{{ $t('reporting.config.approvalStatus') }}</h2>
          <v-radio-group v-model="approvalStatus">
            <v-radio :label="$t('reporting.config.approvedAndReleased')" value="approved" />
            <v-radio :label="$t('reporting.config.notApproved')" value="notApproved" />
          </v-radio-group>
        </div>
      </v-flex>
    </v-layout>

    <div class="mt-6 d-flex">
      <v-spacer />
      <v-btn text class="reporting-action-btn" @click="cancel">{{ $t('actions.cancel') }}</v-btn>
      <v-btn
        color="primary"
        class="ml-2 reporting-action-btn"
        :disabled="!canGenerateReport"
        @click="generateReport"
      >
        {{ $t('reporting.config.generateReport') }}
      </v-btn>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import { mapState, mapActions } from 'vuex';
import WorkpackageSelector from './components/workpackage-selector.vue';
import AttributeSelector from './components/attribute-selector.vue';
import {
  reportingMaxSelectableAttributes,
  reportingMaxAttributeValues,
  reportingMaxSelectableWorkpackages,
} from '@sharedModules/data/enums/feature-flags';
import {
  serializeReportingSelections,
  deserializeReportingSelections,
} from '../../utils/reporting-url-serializer';

export default {
  components: { WorkpackageSelector, AttributeSelector },
  data() {
    return {
      // keep selections local; they are encoded to URL and parsed in view via initialiseFromRoute
      selectedWorkpackages: [],
      selectedAttributes: [],
      approvalStatus: null,
      // Validation state for overlapping pricing groups
      validationLoading: false,
      overlappingPricingGroups: [],
      hasMatchingPricingGroups: true,
    };
  },
  computed: {
    ...mapState('clientConfig', ['toggleLogic']),
    // bind reporting store state directly
    ...mapState('reporting', ['availableAttributes', 'loading']),
    // Always derive ids/keys from local selections to avoid mixing types
    selectedWorkpackageIds() {
      return (this.selectedWorkpackages || []).map(wp => wp._id).filter(Boolean);
    },
    selectedAttributeKeys() {
      return (this.selectedAttributes || []).map(a => a.key).filter(Boolean);
    },
    maxAttributeSelections() {
      return this.toggleLogic[reportingMaxSelectableAttributes];
    },
    maxAttributeValueLimit() {
      return this.toggleLogic[reportingMaxAttributeValues];
    },
    maxWorkpackageSelections() {
      return this.toggleLogic[reportingMaxSelectableWorkpackages];
    },
    hasWorkpackagesSelected() {
      return this.selectedWorkpackageIds.length > 0;
    },
    hasOverlappingPricingGroups() {
      return this.overlappingPricingGroups.length > 0;
    },
    canGenerateReport() {
      return (
        this.hasWorkpackagesSelected &&
        this.selectedAttributeKeys.length > 0 &&
        this.approvalStatus &&
        !this.hasOverlappingPricingGroups &&
        this.hasMatchingPricingGroups
      );
    },
  },
  watch: {
    approvalStatus() {
      this.validateWorkpackageSelection();
    },
    selectedWorkpackages() {
      // Re-validate when workpackages change (e.g., removing a chip)
      if (this.approvalStatus) {
        this.validateWorkpackageSelection();
      }
    },
  },
  async created() {
    // Query-only restore: parse query and hydrate local selections
    const maxWp = this.toggleLogic[reportingMaxSelectableWorkpackages];
    const maxAttr = this.toggleLogic[reportingMaxSelectableAttributes];

    const {
      workpackageIds = [],
      attributeKeys = [],
      approvalStatus = null,
    } = deserializeReportingSelections(this.$route.query || {}, {
      maxWorkpackages: maxWp,
      maxAttributes: maxAttr,
    });

    this.approvalStatus = approvalStatus || null;

    if (workpackageIds.length > 0) {
      // Fire both requests in parallel; await attributes for deterministic hydration
      const attrsPromise = this.fetchIntersectedAttributes({ workpackageIds });
      const wpsPromise = this.fetchReportingWorkpackages({ ids: workpackageIds });

      const attrs = await attrsPromise;
      const available = Array.isArray(attrs) ? attrs : this.availableAttributes || [];
      this.selectedAttributes = available.filter(a =>
        attributeKeys.includes(a.key || a.attributeKey)
      );

      wpsPromise
        .then(items => {
          this.selectedWorkpackages = Array.isArray(items) ? items : [];
          // Validate after workpackages are loaded (for back navigation from report)
          if (this.approvalStatus) {
            this.validateWorkpackageSelection();
          }
        })
        .catch(() => {
          this.selectedWorkpackages = [];
        });
    } else {
      this.selectedWorkpackages = [];
      this.selectedAttributes = [];
    }
  },
  methods: {
    ...mapActions('reporting', ['fetchIntersectedAttributes', 'fetchReportingWorkpackages']),

    cancel() {
      this.$router.push({ path: '/' });
    },
    async onWpMenuClosed() {
      if (!this.hasWorkpackagesSelected) {
        // No workpackages selected: clear attributes to avoid invisible selections
        this.selectedAttributes = [];
        return;
      }
      const workpackageIds = this.selectedWorkpackageIds;
      const attrs = await this.fetchIntersectedAttributes({ workpackageIds });

      // After updating available attributes, drop any selected attributes
      // that are no longer present to avoid invisible-but-selected items
      const available = Array.isArray(attrs) ? attrs : this.availableAttributes || [];
      const selectedKeys = this.selectedAttributeKeys;
      if (selectedKeys && selectedKeys.length > 0 && Array.isArray(available)) {
        this.selectedAttributes = available.filter(a =>
          selectedKeys.includes(a.key || a.attributeKey)
        );
      }
      this.$nextTick(() => {
        const sel = this.$refs.attributeSelector;
        if (sel && typeof sel.focusAndOpen === 'function') {
          sel.focusAndOpen();
        }
      });
      // Validate for overlapping pricing groups
      this.validateWorkpackageSelection();
    },
    async validateWorkpackageSelection() {
      // Reset validation state when no workpackages selected or no approval status
      if (this.selectedWorkpackageIds.length === 0 || !this.approvalStatus) {
        this.overlappingPricingGroups = [];
        this.hasMatchingPricingGroups = true;
        return;
      }

      // For single workpackage, still check if it has matching PGs
      // For 2+ workpackages, also check for overlaps
      this.validationLoading = true;
      try {
        const { data } = await axios.post('/api/reporting/validate-workpackages', {
          workpackageIds: this.selectedWorkpackageIds,
          approved: this.approvalStatus === 'approved',
        });
        this.overlappingPricingGroups = data.overlappingPricingGroups || [];
        this.hasMatchingPricingGroups = data.hasMatchingPricingGroups !== false;
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Workpackage validation failed', error);
        this.overlappingPricingGroups = [];
        this.hasMatchingPricingGroups = true;
      } finally {
        this.validationLoading = false;
      }
    },
    getWorkpackageUrl(workpackageId) {
      return `/pricing/desk-view/workpackage/${workpackageId}`;
    },
    generateReport() {
      const selections = {
        workpackageIds: this.selectedWorkpackageIds,
        attributeKeys: this.selectedAttributeKeys,
        approvalStatus: this.approvalStatus,
      };

      const options = {
        maxWorkpackages: this.maxWorkpackageSelections,
        maxAttributes: this.maxAttributeSelections,
      };

      const query = serializeReportingSelections(selections, options);

      this.$router.push({ name: 'reportingView', query });
    },
  },
};
</script>

<style scoped lang="scss">
.reporting-config-main {
  min-height: calc(100vh - 15rem);
}
.reporting-action-btn {
  font-size: 14px !important;
}
.overlap-error {
  margin-bottom: 4px;
  &:last-child {
    margin-bottom: 0;
  }
}
.overlap-wp-link {
  color: inherit;
  text-decoration: underline;
  &:hover {
    text-decoration: none;
  }
}
</style>
